import React, { useState } from 'react';
import { Plus, X, Save, AlertCircle, Check, FileText, Layers, MousePointer2 } from 'lucide-react';
import { useStore } from '../store';

export function AxisManagement() {
  const { fileData, axes, setAxes, addAxis, removeAxis, addQuestionsToAxis, removeQuestionFromAxis, setCurrentPage } = useStore();
  const [newAxisName, setNewAxisName] = useState('');
  const [selectedQuestions, setSelectedQuestions] = useState<string[]>([]);
  const [currentAxis, setCurrentAxis] = useState<string | null>(Object.keys(axes)[0] || null);
  const [error, setError] = useState<string | null>(null);
  const [showAllQuestions, setShowAllQuestions] = useState<boolean>(false);

  // استخراج جميع الأسئلة من البيانات (طريقة أكثر مرونة)
  const { workshopColumn } = useStore();
  const allQuestions = fileData ? Object.keys(fileData[0]).filter(key => {
    const keyLower = key.toLowerCase().trim();
    // استبعاد عمود الورشة المحدد وبعض الأعمدة الشائعة غير المناسبة للأسئلة
    return key !== workshopColumn && 
           !keyLower.includes('id') &&
           !keyLower.includes('timestamp') &&
           !keyLower.includes('تاريخ') &&
           !keyLower.includes('وقت') &&
           key.length > 3; // تجنب الأعمدة القصيرة جداً
  }) : [];

  // الأسئلة المتاحة (غير المضافة لأي محور)
  const availableQuestions = allQuestions.filter(question => 
    !Object.values(axes).flat().includes(question)
  );

  // الأسئلة المعروضة (حسب الخيار المحدد)
  const displayedQuestions = showAllQuestions ? allQuestions : availableQuestions;

  // إحصائيات مفيدة
  const totalQuestions = allQuestions.length;
  const assignedQuestions = Object.values(axes).flat().length;
  const unassignedQuestions = availableQuestions.length;

  const handleCreateAxis = () => {
    if (!newAxisName.trim()) {
      setError('يرجى إدخال اسم المحور');
      return;
    }
    
    if (axes[newAxisName.trim()]) {
      setError('يوجد محور بهذا الاسم بالفعل');
      return;
    }

    addAxis(newAxisName.trim());
    setCurrentAxis(newAxisName.trim());
    setNewAxisName('');
    setError(null);
  };

  const handleQuestionClick = (question: string) => {
    setSelectedQuestions(prev => {
      if (prev.includes(question)) {
        return prev.filter(q => q !== question);
      } else {
        return [...prev, question];
      }
    });
  };

  const handleAddSelectedQuestions = () => {
    if (!currentAxis) {
      setError('يرجى اختيار محور أولاً');
      return;
    }
    
    if (selectedQuestions.length === 0) {
      setError('يرجى اختيار أسئلة للإضافة');
      return;
    }

    addQuestionsToAxis(currentAxis, selectedQuestions);
    setSelectedQuestions([]);
    setError(null);
  };

  const handleRemoveQuestionFromAxis = (axisName: string, question: string) => {
    removeQuestionFromAxis(axisName, question);
  };

  const handleDeleteAxis = (axisName: string) => {
    removeAxis(axisName);
    if (currentAxis === axisName) {
      const remainingAxes = Object.keys(axes).filter(name => name !== axisName);
      setCurrentAxis(remainingAxes[0] || null);
    }
  };

  const canProceed = Object.keys(axes).length > 0 && Object.values(axes).some(questions => questions.length > 0);

  return (
    <div className="p-6 max-w-6xl mx-auto" dir="rtl">
      <h2 className="text-2xl font-bold mb-6 text-right">إدارة المحاور 📊</h2>

      {/* إنشاء محور جديد */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h3 className="text-lg font-semibold mb-4 text-right flex items-center gap-2">
          <Plus className="w-5 h-5" />
          إنشاء محور جديد
        </h3>
        
        <div className="flex gap-4">
          <button
            onClick={handleCreateAxis}
            className="bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600 flex items-center gap-2"
          >
            <Plus size={20} />
            <span>إنشاء محور</span>
          </button>
          <input
            type="text"
            value={newAxisName}
            onChange={(e) => setNewAxisName(e.target.value)}
            placeholder="اسم المحور الجديد"
            className="flex-1 p-2 border rounded-lg text-right"
            dir="rtl"
            onKeyPress={(e) => e.key === 'Enter' && handleCreateAxis()}
          />
        </div>

        {error && (
          <div className="mt-4 p-3 bg-red-50 text-red-600 rounded-lg flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            <span>{error}</span>
          </div>
        )}
      </div>

      {/* المحاور الموجودة - عرض Tabs */}
      {Object.keys(axes).length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden mb-6">
          {/* شريط التبويبات */}
          <div className="border-b border-gray-200">
            <div className="flex overflow-x-auto" dir="rtl">
              {Object.keys(axes).map((axisName) => (
                <button
                  key={axisName}
                  onClick={() => setCurrentAxis(axisName)}
                  className={`
                    px-6 py-3 text-sm font-medium border-b-2 transition-colors min-w-max
                    ${currentAxis === axisName
                      ? 'border-blue-500 text-blue-600 bg-blue-50'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
                  `}
                >
                  <div className="flex items-center gap-2">
                    <Layers className="w-4 h-4" />
                    <span>{axisName}</span>
                    <span className="bg-gray-200 text-gray-600 px-2 py-1 rounded-full text-xs">
                      {axes[axisName].length}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* محتوى التبويب الحالي */}
          {currentAxis && (
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <button
                  onClick={() => handleDeleteAxis(currentAxis)}
                  className="text-red-500 hover:text-red-700 flex items-center gap-1 text-sm"
                >
                  <X className="w-4 h-4" />
                  حذف المحور
                </button>
                <h4 className="text-lg font-semibold text-right flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  أسئلة محور: {currentAxis}
                </h4>
              </div>

              {/* أسئلة المحور الحالي */}
              <div className="space-y-2">
                {axes[currentAxis].length > 0 ? (
                  axes[currentAxis].map((question, index) => (
                    <div
                      key={question}
                      className="flex items-center gap-3 bg-green-50 p-3 rounded-lg"
                    >
                      <button
                        onClick={() => handleRemoveQuestionFromAxis(currentAxis, question)}
                        className="text-red-500 hover:text-red-700"
                        title="إزالة من المحور"
                      >
                        <X className="w-4 h-4" />
                      </button>
                      <span className="text-green-600 font-medium text-sm">#{index + 1}</span>
                      <span className="flex-1 text-right">{question}</span>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>لم يتم إضافة أسئلة لهذا المحور بعد</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* إحصائيات الأسئلة */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 mb-6">
        <h3 className="text-lg font-semibold text-right mb-3 text-blue-800">📊 إحصائيات الأسئلة</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
          <div className="bg-white rounded-lg p-3 shadow-sm">
            <div className="text-2xl font-bold text-blue-600">{totalQuestions}</div>
            <div className="text-sm text-gray-600">إجمالي الأسئلة</div>
          </div>
          <div className="bg-white rounded-lg p-3 shadow-sm">
            <div className="text-2xl font-bold text-green-600">{assignedQuestions}</div>
            <div className="text-sm text-gray-600">أسئلة مضافة للمحاور</div>
          </div>
          <div className="bg-white rounded-lg p-3 shadow-sm">
            <div className="text-2xl font-bold text-orange-600">{unassignedQuestions}</div>
            <div className="text-sm text-gray-600">أسئلة متاحة للإضافة</div>
          </div>
        </div>
      </div>

      {/* اختيار الأسئلة للإضافة */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-2">
            {selectedQuestions.length > 0 && (
              <>
                <button
                  onClick={handleAddSelectedQuestions}
                  disabled={!currentAxis}
                  className={`
                    px-4 py-2 rounded-lg flex items-center gap-2 text-sm font-medium
                    ${currentAxis 
                      ? 'bg-green-500 text-white hover:bg-green-600' 
                      : 'bg-gray-200 text-gray-500 cursor-not-allowed'}
                  `}
                >
                  <Check className="w-4 h-4" />
                  إضافة ({selectedQuestions.length}) إلى المحور
                </button>
                <button
                  onClick={() => setSelectedQuestions([])}
                  className="text-gray-500 hover:text-gray-700 text-sm"
                >
                  إلغاء التحديد
                </button>
              </>
            )}
          </div>
          <div className="text-right">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <MousePointer2 className="w-5 h-5" />
              اختر الأسئلة للإضافة ({availableQuestions.length} متاح)
              {!currentAxis && <span className="text-red-500 text-sm">(أنشئ محور أولاً)</span>}
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              انقر على الأسئلة لاختيارها، ثم اضغط "إضافة إلى المحور"
            </p>
            
            {/* أزرار التبديل بين عرض الأسئلة */}
            <div className="flex gap-2 mt-3">
              <button
                onClick={() => setShowAllQuestions(false)}
                className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                  !showAllQuestions 
                    ? 'bg-blue-500 text-white' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                المتاحة ({unassignedQuestions})
              </button>
              <button
                onClick={() => setShowAllQuestions(true)}
                className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                  showAllQuestions 
                    ? 'bg-blue-500 text-white' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                جميع الأسئلة ({totalQuestions})
              </button>
            </div>
          </div>
        </div>

        {/* الأسئلة المختارة */}
        {selectedQuestions.length > 0 && (
          <div className="mb-4 p-3 bg-blue-50 rounded-lg">
            <p className="text-blue-800 text-sm mb-2 font-medium">الأسئلة المختارة ({selectedQuestions.length}):</p>
            <div className="flex flex-wrap gap-2">
              {selectedQuestions.map((question, index) => (
                <span key={question} className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm">
                  #{index + 1}: {question.length > 30 ? question.substring(0, 30) + '...' : question}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* قائمة الأسئلة */}
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {displayedQuestions.length > 0 ? (
            displayedQuestions.map((question, index) => {
              const isSelected = selectedQuestions.includes(question);
              const isAssigned = Object.values(axes).flat().includes(question);
              const assignedAxis = isAssigned ? Object.keys(axes).find(axis => 
                axes[axis].includes(question)
              ) : null;
              
              return (
                <div
                  key={question}
                  onClick={() => !isAssigned && handleQuestionClick(question)}
                  className={`
                    p-3 rounded-lg border-2 transition-all
                    ${isSelected
                      ? 'border-blue-500 bg-blue-50 shadow-md transform scale-[1.02] cursor-pointer'
                      : isAssigned 
                        ? 'border-green-200 bg-green-50 opacity-75 cursor-default'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50 cursor-pointer'}
                  `}
                >
                  <div className="flex items-center gap-3">
                    <div className={`
                      w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0
                      ${isSelected
                        ? 'border-blue-500 bg-blue-500'
                        : isAssigned 
                          ? 'border-green-500 bg-green-500'
                          : 'border-gray-300'}
                    `}>
                      {(isSelected || isAssigned) && (
                        <Check className="w-3 h-3 text-white" />
                      )}
                    </div>
                    <span className="text-gray-500 text-sm font-medium shrink-0">#{index + 1}</span>
                    <div className="flex-1 text-right">
                      <span className={`block ${
                        isAssigned ? 'text-green-700' : 'text-gray-800'
                      }`}>
                        {question}
                      </span>
                      {isAssigned && assignedAxis && (
                        <span className="text-xs text-green-600 block mt-1">
                          📌 مضاف إلى: {assignedAxis}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-center py-8 text-gray-500">
              {allQuestions.length === 0 ? (
                <div>
                  <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>لا توجد بيانات محملة</p>
                </div>
              ) : showAllQuestions ? (
                <div>
                  <AlertCircle className="w-12 h-12 mx-auto mb-2 text-yellow-400" />
                  <p>لم يتم العثور على أسئلة في البيانات المحملة</p>
                </div>
              ) : (
                <div>
                  <Check className="w-12 h-12 mx-auto mb-2 text-green-400" />
                  <p>🎉 تم إضافة جميع الأسئلة إلى المحاور!</p>
                  <p className="text-sm mt-2">يمكنك عرض "جميع الأسئلة" لرؤية الأسئلة المضافة</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* أزرار التنقل */}
      <div className="mt-8 flex justify-between">
        <button
          onClick={() => setCurrentPage('file_upload')}
          className="px-6 py-2 rounded-lg text-gray-600 hover:bg-gray-100 border border-gray-300"
        >
          العودة إلى تحميل الملف
        </button>

        <button
          onClick={() => setCurrentPage('scale_config')}
          disabled={!canProceed}
          className={`
            px-8 py-3 rounded-lg flex items-center gap-2 font-semibold
            ${canProceed
              ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:from-blue-600 hover:to-blue-700 shadow-lg'
              : 'bg-gray-200 text-gray-500 cursor-not-allowed'}
          `}
        >
          {canProceed && <Check size={20} />}
          <span>المتابعة إلى إعداد المقياس</span>
          {!canProceed && <AlertCircle size={16} />}
        </button>
      </div>

      {/* رسالة الحالة */}
      {!canProceed && (
        <div className="mt-4 p-4 bg-yellow-50 rounded-lg text-right">
          <p className="text-yellow-800 mb-2 font-medium">
            لا يمكن المتابعة. تأكد من:
          </p>
          <ul className="text-yellow-700 text-sm space-y-1">
            <li>• إنشاء محور واحد على الأقل</li>
            <li>• إضافة أسئلة لكل محور</li>
          </ul>
        </div>
      )}
    </div>
  );
}